import { getNextTextPosition, getPreviousTextPosition } from 'common/Prose/utils'
import { StrongNode, nodes, schema } from 'prose-core'
import { NodeSelection, AllSelection, TextSelection } from 'prosemirror-state'
import { CellSelection } from 'prosemirror-tables'
import { Command } from '../types'
import { Node, NodeType } from 'prosemirror-model'
import { drop, pick } from 'lodash'
/**
 * The general idea of this algorithm is:
 * Make all nodes which are "on the same level" as the node at the start of the
 * selection into one list (that is they have the same parent or if the node at
 * the start of the selection is a list the same parent as the list). If a node
 * is already a list item, keep its attributes and adjust its list type. If not,
 * wrap it in a list item.
 */
export const turnIntoList =
  (type: NodeType, listAttrs?: any): Command =>
  (
    {
      tr,
      doc,
      selection,
      selection: { $from, empty, from, to },
      schema: {
        nodes: { list_item, table, toggle_list, vote_list, vote_list_item },
      },
    },
    dispatch,
  ) => {
    if (
      // Tables cannot be turned into a list
      (selection instanceof NodeSelection && selection.node.type === table) ||
      // Partial table selections cannot be turned into a list
      selection instanceof CellSelection
    ) {
      return false
    }

    // NOTE: all selections are around the doc node, users probably want to go
    // from before the first node to after the last node though, so we
    // "correct" that here
    if (selection instanceof AllSelection) {
      if (doc.nodeSize <= 3) return false

      from = 1
      $from = doc.resolve(from)
    }

    // NOTE: if we are on or before a list item, use the position before the list item as start
    const $start = doc.resolve(
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      $from.node($from.depth - 1)?.type === list_item
        ? $from.before($from.depth - 1)
        : !$from.nodeAfter || $from.nodeAfter.isText
        ? $from.before()
        : $from.pos,
    )
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startNode = $start.nodeAfter!
    const startNodeParent = $start.node()
    const startList = startNode.type === list_item ? $start.node($start.depth - 1) : null

    let maxAfterPos = $start.pos + startNode.nodeSize
    let nodeFound = false
    let listItems: Node[] = []

    doc.nodesBetween(from, to, (node, position, parent) => {
      // NOTE: ignore all "open" nodes this callback runs through until we find our node at the "from" position
      if (node === startNode) nodeFound = true
      if (!nodeFound) return true
      if (node.type === toggle_list) {
        const convertedListItem: Node[] = handleToggleList(node)
        listItems = listItems.concat(convertedListItem)
        // toggleListItems.push(node)
        // const stuff = unwrapToggle(node, { list_item, toggle_list, summary })
        // stuff.forEach((unwrappedToggle) => listItems.push(list_item.create({}, unwrappedToggle)))
        // listItems.push(list_item.create({}, stuff))
        maxAfterPos = Math.max(maxAfterPos, position + node.nodeSize)
        return false
      }

      // NOTE: descend into lists
      if (node.type.groups.includes('list')) return true
      // NOTE: only consider nodes on the same level as the start node OR if thats a list item on the same level as that list
      if (parent !== startNodeParent && parent !== startList && ![list_item, vote_list_item].includes(node.type))
        return false

      // if (node.type === list_item) listItems.push(node)
      if ([list_item, vote_list_item].includes(node.type)) listItems.push(node)
      else {
        if (type === vote_list) {
          listItems.push(vote_list_item.create({ depth: node.attrs.depth }, node))
        } else {
          listItems.push(list_item.create({ depth: node.attrs.depth }, node))
        }
      }

      // NOTE: update the position until which we need to delete
      const afterNodePos = position + node.nodeSize
      if (afterNodePos > maxAfterPos) maxAfterPos = afterNodePos

      return false
    })
    if (type === vote_list) {
      listItems = (listItems as StrongNode<typeof nodes.list_item>[]).map((li) =>
        li.type !== vote_list_item
          ? vote_list_item.create(pick(li.attrs, ['depth', 'uuid', 'trackedChanges']), li.content)
          : li,
      )
    }
    if (type !== vote_list && listItems.some((li) => li.type === vote_list_item)) {
      listItems = (listItems as StrongNode<typeof nodes.vote_list_item>[]).map((vli) =>
        vli.type === vote_list_item
          ? list_item.create(pick(vli.attrs, ['depth', 'uuid', 'trackedChanges']), vli.content)
          : vli,
      )
    }

    tr.replaceRangeWith($start.pos, maxAfterPos, type.create(listAttrs, listItems))

    if (empty) {
      const newSelection = TextSelection.create(tr.doc, (tr.steps[0] as any).from + 2 + ($from.pos - $from.before()))
      tr.setSelection(newSelection)
    } else {
      const listItemsCount = listItems.reduce((count, listItem) => count + listItem.nodeSize, 0)

      const newSelection = TextSelection.create(
        tr.doc,
        getNextTextPosition(tr.doc.resolve((tr.steps[0] as any).from + 1)).pos - 2,
        getPreviousTextPosition(tr.doc.resolve((tr.steps[0] as any).from + listItemsCount + 2)).pos + 2,
      )
      tr.setSelection(newSelection)
    }

    dispatch?.(tr)
    return true
  }

const handleToggleList = (node: Node, depth = 0): Node[] => {
  if (node.type.groups.includes('textWrap') && node.nodeSize === 2) {
    // don't convert empty toggle contents into blank list items
    return []
  }
  let nodesToReturn: Node[] = []
  if (node.type === schema.nodes.toggle_list) {
    nodesToReturn.push(schema.nodes.list_item.create({ depth }, node.content.firstChild?.content.content))
    const remainingNodes = drop(node.content.content, 1)
    const transformedNodes = remainingNodes.map((rn) => handleToggleList(rn, depth + 1)).flat()
    nodesToReturn = nodesToReturn.concat(transformedNodes)
  } else if (node.type === schema.nodes.list_item) {
    const newNode = node.type.create(
      // basically `node.copy` but we need to tweak the depth attribute
      {
        ...node.attrs,
        depth: node.attrs.depth + depth - 1, // we subtract one because we got rid of the wrapper (i.e. unordered_list)
      },
      node.content,
    )
    nodesToReturn.push(newNode)
  } else if (node.type.groups.includes('list')) {
    // if the node is a list, drop the node and process the contents
    nodesToReturn = nodesToReturn.concat(
      (node.content.content ?? []).map((rn) => handleToggleList(rn, depth + 1)).flat(),
    )
  } else {
    nodesToReturn.push(schema.nodes.list_item.create({ depth }, node))
  }
  return nodesToReturn
}
